import EssentialPlanIcon from '@/shared/icons/EssentialPlanIcon';
import GrowthPlanIcon from '@/shared/icons/GrowthPlanIcon';
import AgencyPlanIcon from '@/shared/icons/AgencyPlanIcon';
import FreePlanIcon from '@/shared/icons/FreePlanIcon';

export const ALL_PLAN_CONFIGS = [
  {
    tierType: 'free',
    Icon: FreePlanIcon,
    iconColorClassName: 'text-v3-black-secondary',
    planTitle: 'Free',
    planDescription: 'Build your first AI agent and see what\'s possible.',
    price: '$0',
    buttonLabel: 'Get Started',
    buttonVariant: 'primary',
    cardBackgroundClassName: 'bg-transparent',
  },
  {
    tierType: 'starter',
    Icon: EssentialPlanIcon,
    iconColorClassName: 'text-v3-blue-primary',
    planTitle: 'Starter',
    planDescription: 'Everything you need to deploy a production AI agent.',
    price: '$40',
    buttonLabel: 'Select Starter',
    buttonVariant: 'primary',
    cardBackgroundClassName: 'bg-transparent',
  },
  {
    tierType: 'advanced',
    Icon: GrowthPlanIcon,
    iconColorClassName: 'text-v3-red-primary',
    planTitle: 'Advanced',
    planDescription: 'Scale your AI support with team features and powerful integrations.',
    price: '$100',
    buttonLabel: 'Select Advanced',
    buttonVariant: 'primary',
    cardBackgroundClassName: 'bg-transparent',
  },
  {
    tierType: 'pro',
    Icon: AgencyPlanIcon,
    iconColorClassName: 'text-v3-orange-primary',
    planTitle: 'Pro',
    planDescription: 'Full power for agencies & large teams. Maximum scale, your brand.',
    price: '$400',
    buttonLabel: 'Select Pro',
    buttonVariant: 'primary',
    cardBackgroundClassName: 'bg-transparent',
  },
];

// Keep backward-compat alias for any existing imports
export const FREE_TIER_PLANS = ALL_PLAN_CONFIGS.filter((p) => p.tierType !== 'free');

export const PERIOD_FILTERS = [
  { value: 'monthly', label: 'Monthly Plans' },
  { value: 'yearly', label: 'Annual Plans (2 months free)' },
];

export const TIER_ORDER = { free: 0, starter: 1, advanced: 2, pro: 3 };

export const TIER_FEATURES = [
  {
    tier_types: ['free'],
    name: 'Free',
    key_features: [
      '1 AI agent',
      '100 credits/month',
      '250K character knowledge base',
      'Custom personality & branding',
      'Embed on any website',
      'Upload websites & files',
      'Discord community support',
    ],
  },
  {
    tier_types: ['starter', 'entry'],
    name: 'Starter',
    key_features: [
      '2 AI agents',
      '3,000 credits/month',
      '25M character knowledge base',
      'Chat logs & saved conversations',
      'Voice input & realtime voice',
      '1 phone number included',
      'Source citations',
      'Analytics dashboard',
      'All AI models (Claude, GPT-5.4, Gemini Pro)',
      'Zapier, Google Drive, and 7,000+ integrations',
      'Enhanced security',
      '1 custom domain',
      'Priority support',
    ],
  },
  {
    tier_types: ['advanced', 'premium', 'professional'],
    name: 'Advanced',
    key_features: [
      '20 AI agents',
      '12,500 credits/month',
      '50M character knowledge base',
      'AI lead generation & data collection',
      'Human handover (10 live agents)',
      'WhatsApp, Calendly integrations',
      'Image display in responses',
      'Remove Dante branding',
      'Custom CSS',
      'Auto-refresh knowledge sources',
      'Rate limiting',
      'Team management (10 seats) with custom roles',
      '50 custom domains',
      'Premium support',
    ],
  },
  {
    tier_types: ['pro', 'ultra', 'business'],
    name: 'Pro',
    key_features: [
      '50 AI agents',
      '50,000 credits/month',
      '200M character knowledge base',
      'Use your own Open AI Key',
      '50 voice agents & phone numbers',
      'Daily auto-refresh',
      'Reseller-friendly',
      'API setup support',
      'Uptime SLA',
      'Dedicated account manager',
    ],
  },
  {
    tier_types: ['enterprise'],
    name: 'Enterprise',
    key_features: [
      'Unlimited AI agents',
      'Unlimited responses/month',
      'Unlimited memory',
      'Custom integrations',
      'Unlimited team seats',
      'Unlimited custom domains',
    ],
  },
];

/** Maps feature keys (from featureCheck) to their display labels in TIER_FEATURES key_features arrays */
export const FEATURE_KEY_TO_LABEL = {
  // Starter features
  chatbots_limit: '2 AI agents',
  number_of_knowledgebases: '2 AI agents',
  credits: '3,000 credits/month',
  max_characters: '25M character knowledge base',
  share_conversations: 'Chat logs & saved conversations',
  voice_to_text: 'Voice input & realtime voice',
  realtime_voice_access: 'Voice input & realtime voice',
  sources: 'Source citations',
  analytics_dashboard: 'Analytics dashboard',
  advanced_gpt_models: 'All AI models (Claude, GPT-5.4, Gemini Pro)',
  claude_model: 'All AI models (Claude, GPT-5.4, Gemini Pro)',
  cohere_model: 'All AI models (Claude, GPT-5.4, Gemini Pro)',
  zapier_integration: 'Zapier, Google Drive, and 7,000+ integrations',
  google_drive_integration: 'Zapier, Google Drive, and 7,000+ integrations',
  enhanced_chatbot_security: 'Enhanced security',
  custom_url: '1 custom domain',
  // Advanced features
  data_collection: 'AI lead generation & data collection',
  live_agent: 'Human handover (10 live agents)',
  whatsapp_integration: 'WhatsApp, Calendly integrations',
  calendly_integration: 'WhatsApp, Calendly integrations',
  remove_powered_by_dante: 'Remove Dante branding',
  remove_watermark: 'Remove Dante branding',
  custom_css: 'Custom CSS',
  auto_refresh_training: 'Auto-refresh knowledge sources',
  rate_limiting: 'Rate limiting',
  team_management: 'Team management (10 seats) with custom roles',
  add_custom_roles: 'Team management (10 seats) with custom roles',
  // Pro features
  user_openai_api_key: 'Use your own Open AI Key',
  api_access: 'API setup support',
};

export const getTierFeaturesConfig = (tierType) => {
  const key = (tierType || '').toLowerCase();
  return (
    TIER_FEATURES.find((tier) => tier.tier_types.includes(key)) ||
    TIER_FEATURES[1]
  );
};

export const COMPARISON_TABLE_DATA = {
  'Core Limits': {
    'Credits Per Month': { free: '100', starter: '3,000', advanced: '12,500', pro: '50,000' },
    'AI Agents': { free: '1', starter: '2', advanced: '20', pro: '50' },
    'Knowledge Base Size': { free: '250K chars', starter: '25M chars', advanced: '50M chars', pro: '200M chars' },
    'Custom Domains': { free: false, starter: '1', advanced: '50', pro: '50' },
  },
  'AI Models': {
    'Fast Models (GPT-4.1 Nano/Mini, Gemini Flash)': { free: true, starter: true, advanced: true, pro: true },
    'Cohere Access': { free: false, starter: true, advanced: true, pro: true },
    'Advanced LLMs (Claude, GPT-5.4, Gemini Pro)': { free: false, starter: true, advanced: true, pro: true },
    'Use your own Open AI Key': { free: false, starter: false, advanced: false, pro: true },
  },
  'Chat & Conversations': {
    'Share Conversations': { free: false, starter: true, advanced: true, pro: true },
    'Chat Records': { free: false, starter: true, advanced: true, pro: true },
    'Save Conversations': { free: false, starter: true, advanced: true, pro: true },
    'Source Citations': { free: false, starter: true, advanced: true, pro: true },
    'Voice to Text': { free: false, starter: true, advanced: true, pro: true },
    'AI Lead Gen / Data Collection': { free: false, starter: false, advanced: true, pro: true },
    'Image Display in Responses': { free: false, starter: false, advanced: true, pro: true },
    'Unanswered Questions': { free: false, starter: false, advanced: true, pro: true },
  },
  'Agent Customization': {
    'Custom Personality & Branding': { free: true, starter: true, advanced: true, pro: true },
    'Embed on Unlimited Websites': { free: true, starter: true, advanced: true, pro: true },
    'Enhanced Security': { free: false, starter: true, advanced: true, pro: true },
    'Remove Watermark': { free: false, starter: false, advanced: true, pro: true },
    'Custom CSS': { free: false, starter: false, advanced: true, pro: true },
  },
  'Integrations': {
    'Google Drive': { free: false, starter: true, advanced: true, pro: true },
    'Zapier': { free: false, starter: true, advanced: true, pro: true },
    'Slack (Zapier)': { free: false, starter: true, advanced: true, pro: true },
    'MS Teams (Zapier)': { free: false, starter: true, advanced: true, pro: true },
    'Discord (Zapier)': { free: false, starter: true, advanced: true, pro: true },
    'WhatsApp': { free: false, starter: false, advanced: true, pro: true },
    'Calendly': { free: false, starter: false, advanced: true, pro: true },
  },
  'Auto-Refresh': {
    'Auto-Refresh Enabled': { free: false, starter: false, advanced: true, pro: true },
    'Frequency': { free: false, starter: false, advanced: 'Every 3 days / Weekly', pro: 'Daily / Every 3 days / Weekly' },
  },
  'API & Analytics': {
    'Analytics Dashboard': { free: false, starter: true, advanced: true, pro: true },
    'Rate Limiting Config': { free: false, starter: false, advanced: true, pro: true },
    'API Access': { free: false, starter: false, advanced: false, pro: true },
  },
  'Human Handover': {
    'Human Handover': { free: false, starter: false, advanced: true, pro: true },
    'Max Live Agents': { free: false, starter: false, advanced: '10', pro: '100' },
  },
  'Team Management': {
    'Team Management': { free: false, starter: true, advanced: true, pro: true },
    'Max Team Members': { free: false, starter: 'Add-on only', advanced: '10', pro: '10' },
    'Custom Roles': { free: false, starter: false, advanced: true, pro: true },
  },
  'AI Voice': {
    'Max Voice Agents': { free: '1', starter: '5', advanced: '10', pro: '50' },
    'Max Phone Numbers': { free: '1', starter: '5', advanced: '10', pro: '50' },
    'Realtime Voice Access': { free: false, starter: true, advanced: true, pro: true },
    'Free Phone Numbers': { free: false, starter: '1', advanced: '1', pro: '1' },
  },
  'Support': {
    'Discord Community Support': { free: true, starter: true, advanced: true, pro: true },
    'Priority Support': { free: false, starter: true, advanced: true, pro: true },
    'Premium Support': { free: false, starter: false, advanced: true, pro: true },
    'API Setup Support': { free: false, starter: false, advanced: true, pro: true },
    'Reseller Friendly': { free: false, starter: false, advanced: true, pro: true },
    'Uptime SLA Guarantee': { free: false, starter: false, advanced: false, pro: true },
    'Dedicated Account Manager': { free: false, starter: false, advanced: false, pro: true },
  },
};

export const ADDONS_CONFIG = [
  {
    name: 'Extra Messages',
    price: 'From $75/mo',
    description: '$75 for up to 10,000 messages/month. Multiple pack sizes available.',
    availability: 'All paid plans',
  },
  {
    name: 'Extra Agents',
    price: '$5/mo',
    description: 'Add another AI agent with its own knowledge base, personality, and deployment.',
    availability: 'All paid plans',
  },
  {
    name: 'Extra Seat',
    price: '$9/mo',
    description: 'Add team members to manage your AI agents together.',
    availability: 'All paid plans',
  },
  {
    name: 'Custom Domain',
    price: '$5/mo',
    description: 'Replace the Dante AI URL in your share links.',
    availability: 'All paid plans',
  },
  {
    name: 'Remove Branding',
    price: '$29/mo',
    description: "Remove 'Powered by Dante AI' branding from agents. Included free on Advanced+.",
    availability: 'Starter only',
  },
  {
    name: 'Chat Records',
    price: '$49/mo',
    description: 'Retrieve the complete chat history from your external agents.',
    availability: 'Starter only',
  },
];

export const ENTERPRISE_FEATURES = [
  'Unlimited everything',
  'Custom SLA',
  'Dedicated support',
  'SSO & onboarding',
];

export const FAQ_DATA = [
  {
    question: 'What are credits and how do they work?',
    answer: 'Credits are the currency that powers your AI agents. One credit is consumed each time your agent sends a response, and the cost per response depends on the AI model you choose.\n\nLightweight models like GPT-4.1 Nano cost as little as 0.15 credits per response, while advanced models like Claude Sonnet and GPT-5.4 cost more. You can check each model\'s exact credit cost in the model selector when configuring your agent.\n\nFor example, on the Starter plan with 3,000 monthly credits using the cheapest model, you could get up to 20,000 responses per month.',
  },
  {
    question: 'What happens when I run out of credits?',
    answer: 'When your credits run out, your AI agents will stop responding until your credits reset at the start of your next billing cycle. You won\'t lose any data or settings.\n\nTo avoid downtime, you can purchase additional credit packs starting at $75 for 10,000 credits, or upgrade to a higher plan for a larger monthly credit allowance. You can also monitor your credit usage in the dashboard to plan ahead.',
  },
  {
    question: 'Can I switch plans anytime?',
    answer: 'Yes, you can switch plans at any time with no long term contracts. When you upgrade, your new features and higher credit allowance are available immediately.\n\nIf you choose to downgrade, the change takes effect at the end of your current billing cycle so you can continue using your current plan until then. You can also cancel your subscription at any time from your account settings.',
  },
  {
    question: "What's the difference between Starter and Advanced?",
    answer: 'Starter is perfect for getting started with up to 2 production ready AI agents. It includes all AI models (Claude, GPT-5.4, Gemini Pro), voice capabilities, analytics, source citations, and integrations like Google Drive. Through Zapier, you also get access to Slack, MS Teams, Discord, and 7,000+ integrations.\n\nAdvanced unlocks 20 agents, human handover with up to 10 live agents, WhatsApp and Calendly integrations, auto-refresh for your knowledge sources, up to 10 team seats with custom roles, and removes Dante branding from your agent.',
  },
  {
    question: 'What if I need more agents or team seats?',
    answer: 'All paid plans support add-ons so you can scale as you grow. You can add extra AI agents for $5/month each and extra team seats for $9/month each. Each additional agent gets its own knowledge base, personality, and deployment.\n\nYou can manage your add-ons at any time from your billing page, and changes take effect immediately.',
  },
  {
    question: 'Do you offer annual billing?',
    answer: 'Yes, we offer annual billing on all paid plans. When you choose annual billing, you get 2 months free compared to paying monthly. You can switch between monthly and annual billing at any time from your billing page.',
  },
  {
    question: 'Which AI models can I use?',
    answer: 'All plans include fast models like GPT-4.1 Nano, GPT-4.1 Mini, GPT-4o Mini, Gemini Flash, and Claude Haiku. On any paid plan, you also get access to all advanced models including Claude Sonnet, Claude Opus, GPT-5.4, Gemini Pro, and Cohere.\n\nThe Pro plan additionally gives you the option to bring your own OpenAI API key for full control over your model usage.',
  },
  {
    question: 'Is there a free plan?',
    answer: 'Yes, the Free plan lets you build and deploy one AI agent with 100 monthly credits and a 250K character knowledge base. No credit card is required to get started.\n\nIt is a great way to explore the platform and test your AI agent before committing to a paid plan. You can upgrade whenever you need more credits, features, or agents.',
  },
  {
    question: 'What happens to my data if I downgrade or cancel?',
    answer: 'If you downgrade, you will need to adjust your account to fit within the limits of your new plan. For example, if your new plan includes fewer AI agents, extra agents and their data will be removed. The same applies to team members if your new plan has fewer seats.\n\nIf you cancel your subscription, your account will revert to the Free plan and the same limits will apply. We recommend exporting any important data before making changes.',
  },
];

export const TRUST_BAR_ITEMS = [
  { label: 'Excellent on Trustpilot', icon: 'star' },
  { label: 'Cancel anytime', icon: 'check' },
  { label: 'No commitment', icon: 'clock' },
];
